import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, tokens } from '@/design';
import type { LayoutPicturePatch } from './layoutDocument';
import { SymbolColorPicker } from './SymbolColorPicker';
import {
  layoutPictureSourceFromFile,
  layoutPictureSourceFromUrl,
  PICTURE_ACCEPT,
} from './layoutPicture';
import { MM_PER_INCH } from './pageUnits';
import type { LayoutPictureElement, PageRect, PageUnits } from './layoutTypes';

export interface PicturePropertiesPanelProps {
  readonly picture: LayoutPictureElement;
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly pageUnits: PageUnits;
  readonly onChange: (patch: LayoutPicturePatch) => void;
  readonly onRectChange: (rect: PageRect) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

type Tab = 'options' | 'display' | 'placement';

function bounded(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
}

function Section({
  title,
  open = false,
  children,
}: {
  readonly title: string;
  readonly open?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <details open={open}>
      <summary
        style={{
          minHeight: tokens.density.rowDense,
          cursor: 'pointer',
          fontSize: tokens.font.size.md,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        {title}
      </summary>
      <div
        style={{
          display: 'grid',
          gap: tokens.space[2],
          padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
        }}
      >
        {children}
      </div>
    </details>
  );
}

export function PicturePropertiesPanel({
  picture,
  pageWidthMm,
  pageHeightMm,
  pageUnits,
  onChange,
  onRectChange,
  onDelete,
  onClose,
}: PicturePropertiesPanelProps) {
  const [tab, setTab] = useState<Tab>('options');
  const [url, setUrl] = useState(picture.source.href ?? '');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const disabled = picture.locked;
  const unitFactor = pageUnits === 'in' ? MM_PER_INCH : 1;
  const unitLabel = pageUnits === 'in' ? 'in' : 'mm';

  const field: CSSProperties = {
    width: '100%',
    minHeight: tokens.density.rowDense,
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.8}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const check: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const tabStyle = (active: boolean): CSSProperties => ({
    flex: 1,
    minHeight: tokens.density.rowDefault,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  });
  const numberField = (
    label: string,
    value: number,
    min: number,
    max: number,
    onValue: (value: number) => void,
    step = 0.5,
  ) => (
    <label style={row}>
      <span>{label}</span>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onValue(bounded(event.target.value, min, max, value))}
        style={field}
      />
    </label>
  );
  const replaceFromFile = async (file: File) => {
    try {
      setError(null);
      onChange({ source: await layoutPictureSourceFromFile(file) });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to replace picture');
    }
  };
  const replaceFromUrl = async () => {
    try {
      setError(null);
      onChange({ source: await layoutPictureSourceFromUrl(url) });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to replace picture');
    }
  };

  return (
    <aside
      data-testid="picture-properties-panel"
      aria-label="Picture properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[3],
        border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
            Element
          </div>
          <strong>{picture.name}</strong>
        </div>
        <div style={{ display: 'flex' }}>
          <button type="button" aria-label="Delete picture" disabled={disabled} onClick={onDelete} style={iconButton}>
            <Icon name="delete" size={20} decorative />
          </button>
          <button type="button" aria-label="Close picture properties" onClick={onClose} style={iconButton}>
            <Icon name="close" size={20} decorative />
          </button>
        </div>
      </div>
      <div
        role="tablist"
        aria-label="Picture property tabs"
        style={{ display: 'flex', gap: tokens.space[3], marginTop: tokens.space[2] }}
      >
        {(['options', 'display', 'placement'] as const).map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={tab === candidate}
            onClick={() => setTab(candidate)}
            style={tabStyle(tab === candidate)}
          >
            {candidate === 'options' ? 'Options' : candidate === 'display' ? 'Display' : 'Placement'}
          </button>
        ))}
      </div>
      {tab === 'options' ? (
        <div style={{ display: 'grid', gap: tokens.space[3], marginTop: tokens.space[3] }}>
          <Section title="General" open>
            <label style={row}>
              <span>Name</span>
              <input
                aria-label="Picture name"
                value={picture.name}
                disabled={disabled}
                onChange={(event) => onChange({ name: event.target.value })}
                style={field}
              />
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Picture visible"
                checked={picture.visible}
                onChange={(event) => onChange({ visible: event.target.checked })}
              />
              Visible
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Picture locked"
                checked={picture.locked}
                onChange={(event) => onChange({ locked: event.target.checked })}
              />
              Locked
            </label>
          </Section>
          <Section title="Source" open>
            <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
              {picture.source.kind === 'url' ? 'URL' : 'File'} · {picture.source.name}
              {' · '}
              {picture.source.naturalWidthPx}×{picture.source.naturalHeightPx} px
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={PICTURE_ACCEPT}
              aria-label="Replace picture from file"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void replaceFromFile(file);
              }}
            />
            <button
              type="button"
              aria-label="Change picture from local or network path"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              style={{ ...field, cursor: 'pointer' }}
            >
              From local or network path
            </button>
            <label style={{ display: 'grid', gap: tokens.space[1], fontSize: tokens.font.size.sm }}>
              From URL
              <input
                aria-label="Replace picture URL"
                value={url}
                disabled={disabled}
                onChange={(event) => setUrl(event.target.value)}
                style={field}
              />
            </label>
            <button
              type="button"
              aria-label="Change picture from URL"
              disabled={disabled}
              onClick={() => void replaceFromUrl()}
              style={{ ...field, cursor: 'pointer' }}
            >
              Apply URL
            </button>
            {error ? (
              <div role="alert" style={{ color: tokens.color.semantic.danger, fontSize: tokens.font.size.sm }}>
                {error}
              </div>
            ) : null}
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Preserve picture aspect ratio"
                checked={picture.preserveAspectRatio}
                disabled={disabled}
                onChange={(event) => onChange({ preserveAspectRatio: event.target.checked })}
              />
              Preserve aspect ratio
            </label>
          </Section>
        </div>
      ) : null}
      {tab === 'display' ? (
        <div style={{ display: 'grid', gap: tokens.space[3], marginTop: tokens.space[3] }}>
          <Section title="Picture" open>
            {numberField('Opacity', picture.opacityPercent, 0, 100, (opacityPercent) =>
              onChange({ opacityPercent }), 1)}
          </Section>
          {(['background', 'border', 'shadow'] as const).map((part) => {
            const symbol = picture[part];
            return (
              <Section key={part} title={part[0].toUpperCase() + part.slice(1)} open={part === 'border'}>
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label={`${part} enabled`}
                    checked={symbol.enabled}
                    disabled={disabled}
                    onChange={(event) => onChange({ [part]: { enabled: event.target.checked } })}
                  />
                  Enabled
                </label>
                <div style={row}>
                  <span>Fill</span>
                  <SymbolColorPicker
                    label={`${part} fill`}
                    value={symbol.color}
                    disabled={disabled}
                    onChange={(color) => onChange({ [part]: { color: color ?? symbol.color } })}
                  />
                </div>
                {part !== 'shadow' ? (
                  <>
                    {numberField('X gap', symbol.gapXPt, 0, 48, (gapXPt) => onChange({ [part]: { gapXPt } }))}
                    {numberField('Y gap', symbol.gapYPt, 0, 48, (gapYPt) => onChange({ [part]: { gapYPt } }))}
                    {numberField('Outline width', symbol.outlineWidthPt, 0, 24, (outlineWidthPt) =>
                      onChange({ [part]: { outlineWidthPt } }))}
                    <div style={row}>
                      <span>Outline</span>
                      <SymbolColorPicker
                        label={`${part} outline`}
                        value={symbol.outlineColor}
                        disabled={disabled}
                        onChange={(outlineColor) =>
                          onChange({ [part]: { outlineColor: outlineColor ?? symbol.outlineColor } })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {numberField('Offset X', symbol.offsetXPt, -48, 48, (offsetXPt) =>
                      onChange({ shadow: { offsetXPt } }))}
                    {numberField('Offset Y', symbol.offsetYPt, -48, 48, (offsetYPt) =>
                      onChange({ shadow: { offsetYPt } }))}
                  </>
                )}
              </Section>
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: tokens.space[3], marginTop: tokens.space[3] }}>
          <Section title="Size and position" open>
            {numberField('X', Number((picture.rect.x / unitFactor).toFixed(2)), 0, pageWidthMm / unitFactor, (value) =>
              onRectChange({ ...picture.rect, x: value * unitFactor }))}
            {numberField('Y', Number((picture.rect.y / unitFactor).toFixed(2)), 0, pageHeightMm / unitFactor, (value) =>
              onRectChange({ ...picture.rect, y: value * unitFactor }))}
            {numberField(
              `Width (${unitLabel})`,
              Number((picture.rect.width / unitFactor).toFixed(2)),
              1,
              pageWidthMm / unitFactor,
              (value) => {
                const width = value * unitFactor;
                const height = picture.preserveAspectRatio
                  ? width * (picture.rect.height / picture.rect.width)
                  : picture.rect.height;
                onRectChange({ ...picture.rect, width, height });
              },
            )}
            {numberField(
              `Height (${unitLabel})`,
              Number((picture.rect.height / unitFactor).toFixed(2)),
              1,
              pageHeightMm / unitFactor,
              (value) => {
                const height = value * unitFactor;
                const width = picture.preserveAspectRatio
                  ? height * (picture.rect.width / picture.rect.height)
                  : picture.rect.width;
                onRectChange({ ...picture.rect, width, height });
              },
            )}
            {numberField('Rotation', picture.rotation, -360, 360, (rotation) => onChange({ rotation }), 1)}
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Flip picture horizontally"
                checked={picture.flipX}
                disabled={disabled}
                onChange={(event) => onChange({ flipX: event.target.checked })}
              />
              Flip horizontal
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Flip picture vertically"
                checked={picture.flipY}
                disabled={disabled}
                onChange={(event) => onChange({ flipY: event.target.checked })}
              />
              Flip vertical
            </label>
          </Section>
        </div>
      )}
    </aside>
  );
}
